import React, { useState } from 'react';
import { Form, Input, Button, Alert, Row, Col, App } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import supportService from '../../services/support';

const SHOW_ADDRESS_FIELDS = false;

export default function SupportContactForm() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [globalError, setGlobalError] = useState(null);
  const { message } = App.useApp();

  const handleInputChange = () => {
    if (globalError) {
      setGlobalError(null);
    }
  };

  const buildPayload = (values) => ({
    email: values.email,
    phone: values.phone || undefined,
    firstName: values.firstName,
    lastName: values.lastName,
    secondLastName: values.secondLastName,
    postalCode: values.postalCode || undefined,
    address: values.address || undefined,
    message: values.message,
  });

  const onFinish = async (values) => {
    setLoading(true);
    setGlobalError(null);

    try {
      let result =  await supportService.submitSupportTicket(buildPayload(values));
      console.log(result);
      message.success('Tu consulta fue enviada correctamente. Te contactaremos al correo registrado.');
      form.resetFields();
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data;

      if (status === 422) {
        const fieldErrors = data?.fieldErrors || data?.errors || {};
        const fields = Object.entries(fieldErrors).map(([name, errors]) => ({
          name,
          errors: Array.isArray(errors) ? errors : [String(errors || 'Error')],
        }));

        if (fields.length) {
          form.setFields(fields);
        } else {
          setGlobalError(data?.message || 'Revisa los datos del formulario e inténtalo de nuevo.');
        }
      } else if (status >= 500) {
        setGlobalError('Error del servidor. Por favor, inténtalo más tarde.');
      } else if (!navigator.onLine) {
        setGlobalError('Sin conexión a internet. Verifica tu conexión e inténtalo de nuevo.');
      } else {
        setGlobalError(data?.message || err?.message || 'No se pudo enviar tu consulta. Inténtalo de nuevo.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="support-form-card">
      {globalError && (
        <Alert
          type="error"
          showIcon
          icon={<ExclamationCircleOutlined />}
          style={{ marginBottom: 16 }}
          message="Error al enviar"
          description={globalError}
          closable
          onClose={() => setGlobalError(null)}
        />
      )}

      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        onFinish={onFinish}
        validateTrigger={['onBlur', 'onSubmit']}
        initialValues={{ postalCode: '', address: '' }}
      >
        <Form.Item
          label="Correo electrónico"
          name="email"
          rules={[
            { required: true, message: 'El correo electrónico es obligatorio.' },
            { type: 'email', message: 'Formato de correo inválido.' },
          ]}
        >
          <Input
            size="large"
            placeholder="tu@email.com"
            autoComplete="email"
            onChange={handleInputChange}
          />
        </Form.Item>

        <Form.Item label="Celular" name="phone">
          <Input
            size="large"
            placeholder="10 dígitos"
            autoComplete="tel"
            onChange={handleInputChange}
          />
        </Form.Item>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              label="Nombre(s)"
              name="firstName"
              rules={[{ required: true, message: 'El nombre es obligatorio.' }]}
            >
              <Input size="large" placeholder="Nombre(s)" onChange={handleInputChange} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              label="Primer apellido"
              name="lastName"
              rules={[{ required: true, message: 'El primer apellido es obligatorio.' }]}
            >
              <Input size="large" placeholder="Primer apellido" onChange={handleInputChange} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label="Segundo apellido"
          name="secondLastName"
          rules={[{ required: true, message: 'El segundo apellido es obligatorio.' }]}
        >
          <Input size="large" placeholder="Segundo apellido" onChange={handleInputChange} />
        </Form.Item>

        {SHOW_ADDRESS_FIELDS ? (
          <>
            <Form.Item label="Código postal" name="postalCode">
              <Input size="large" placeholder="Código postal" onChange={handleInputChange} />
            </Form.Item>

            <Form.Item label="Dirección" name="address">
              <Input
                size="large"
                placeholder="Calle, número, col, estado, ciudad"
                onChange={handleInputChange}
              />
            </Form.Item>
          </>
        ) : (
          <>
            <Form.Item name="postalCode" hidden>
              <Input />
            </Form.Item>

            <Form.Item name="address" hidden>
              <Input />
            </Form.Item>
          </>
        )}

        <Form.Item
          label="Describe tu consulta"
          name="message"
          rules={[{ required: true, message: 'La descripción de la consulta es obligatoria.' }]}
        >
          <Input.TextArea
            rows={5}
            placeholder="Cuéntanos en detalle tu consulta o problema"
            onChange={handleInputChange}
          />
        </Form.Item>

        <Form.Item style={{ marginBottom: 0 }}>
          <Button type="primary" htmlType="submit" size="large" loading={loading} block>
            Enviar consulta
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
}
